<template>
  <div class="recommendation-container">
    <div class="header">
      <h1>Recommendation Matrix</h1>
      <div class="header-actions">
        <div class="search-container">
          <input 
            type="text" 
            v-model="searchQuery" 
            @input="handleSearch" 
            placeholder="Search recommendations..."
            class="search-input"
          />
        </div>
        <button @click="showCreateModal = true" class="create-button">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 5v14M5 12h14"/>
          </svg>
          Add New Recommendation
        </button>
      </div>
    </div>

    <div class="statistics-grid">
      <div class="stat-card">
        <div class="stat-icon total-recommendations">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
          </svg>
        </div>
        <div class="stat-info">
          <h3>Total Recommendations</h3>
          <p>{{ recommendationData.length }}</p>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon top-courses">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
          </svg>
        </div>
        <div class="stat-info">
          <h3>Top Courses</h3>
          <p>{{ recommendationData.filter(item => item.course.top_course).length }}</p>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon breathing">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
            <line x1="9" y1="9" x2="9.01" y2="9"/>
            <line x1="15" y1="9" x2="15.01" y2="9"/>
          </svg>
        </div>
        <div class="stat-info">
          <h3>Breathing Courses</h3>
          <p>{{ recommendationData.filter(item => item.course.is_breathing).length }}</p>
        </div>
      </div>
    </div>

    <div class="content">
      <div v-if="loading" class="loading">
        <div class="spinner"></div>
        <p>Loading recommendation data...</p>
      </div>

      <div v-else-if="error" class="error">
        <p>{{ error }}</p>
        <button @click="fetchRecommendations" class="retry-button">Retry</button>
      </div>

      <div v-else class="table-container">
        <table>
          <thead>
            <tr>
              <th>Course Information</th>
              <th class="hidden sm:table-cell">Duration</th>
              <th class="hidden sm:table-cell">Option</th>
              <th class="hidden md:table-cell">Category</th>
              <th class="hidden md:table-cell">Instructor</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in recommendationData" :key="item.id" class="hover:bg-gray-50 transition-colors">
              <td>
                <div class="course-info">
                  <span class="course-title">{{ item.course.title }}</span>
                  <!-- Mobile-only info -->
                  <div class="sm:hidden space-y-1 mt-1.5 mobile-info">
                    <div>Duration: {{ formatDuration(item.course.minutes) }}</div>
                    <div>Option: {{ item.option.value }}</div>
                    <div>Category: {{ item.course.category_id }}</div>
                    <div>Instructor: {{ item.course.instructor_id }}</div>
                  </div>
                </div>
              </td>
              <td class="hidden sm:table-cell">
                {{ formatDuration(item.course.minutes) }}
              </td>
              <td class="hidden sm:table-cell">
                {{ item.option.value }}
              </td>
              <td class="hidden md:table-cell">
                <span class="category-badge">
                  {{ item.course.category_id }}
                </span>
              </td>
              <td class="hidden md:table-cell">
                {{ item.course.instructor_id }}
              </td>
              <td>
                <div class="status-badges">
                  <span v-if="item.course.top_course" class="status-badge top-course">
                    Top Course
                  </span>
                  <span v-if="item.course.is_breathing" class="status-badge breathing">
                    Breathing
                  </span>
                </div>
              </td>
              <td>
                <div class="actions">
                  <button @click="openEditModal(item)" class="action-btn edit">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                  </button>
                  <button @click="deleteRecommendation(item.id)" class="action-btn delete">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M3 6h18"/>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Create Modal -->
    <div v-if="showCreateModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h2>Create New Recommendation</h2>
          <button @click="showCreateModal = false" class="close-button">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>
        
        <div class="modal-body">
          <div class="form-group">
            <label>Course ID</label>
            <input 
              v-model="newRecommendation.course_id" 
              type="number" 
              class="form-input"
              placeholder="Enter course ID"
            >
          </div>
          
          <div class="form-group">
            <label>Option ID</label>
            <input 
              v-model="newRecommendation.option_id" 
              type="number" 
              class="form-input"
              placeholder="Enter option ID"
            >
          </div>

          <div class="modal-actions">
            <button 
              @click="showCreateModal = false"
              class="cancel-button"
            >
              Cancel
            </button>
            <button 
              @click="createRecommendation"
              :disabled="creating"
              class="submit-button"
            >
              {{ creating ? 'Creating...' : 'Create' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Modal -->
    <div v-if="showEditModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h2>Edit Recommendation</h2>
          <button @click="showEditModal = false" class="close-button">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>
        
        <div class="modal-body">
          <div class="form-group">
            <label>Course ID</label>
            <input 
              v-model="editingRecommendation.course_id" 
              type="number" 
              class="form-input"
              placeholder="Enter course ID"
            >
          </div>
          
          <div class="form-group">
            <label>Option ID</label>
            <input 
              v-model="editingRecommendation.option_id" 
              type="number" 
              class="form-input"
              placeholder="Enter option ID"
            >
          </div>

          <div class="modal-actions">
            <button 
              @click="showEditModal = false"
              class="cancel-button"
            >
              Cancel
            </button>
            <button 
              @click="updateRecommendation"
              :disabled="updating"
              class="submit-button"
            >
              {{ updating ? 'Updating...' : 'Update' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { debounce } from 'lodash';

export default {
  name: 'RecommendationMatrix',
  data() {
    return {
      recommendationData: [],
      loading: true,
      error: null,
      showCreateModal: false,
      showEditModal: false,
      creating: false,
      updating: false,
      searchQuery: '',
      newRecommendation: {
        course_id: null,
        option_id: null
      },
      editingRecommendation: {
        id: null,
        course_id: null,
        option_id: null
      }
    };
  },
  methods: {
    formatDuration(minutes) {
      if (!minutes) return '0 mins';
      if (minutes < 60) return `${minutes} mins`;
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return remainingMinutes > 0 
        ? `${hours}h ${remainingMinutes}m` 
        : `${hours}h`;
    },
    openEditModal(item) {
      this.editingRecommendation = {
        id: item.id,
        course_id: item.course.id,
        option_id: item.option.id
      };
      this.showEditModal = true;
    },
    async updateRecommendation() {
      if (!this.editingRecommendation.course_id || !this.editingRecommendation.option_id) {
        alert('Please fill in all fields');
        return;
      }

      this.updating = true;
      try {
        const response = await axios.put(
          `https://api.kewaar.com/api/admin/recommendation-matrix/${this.editingRecommendation.id}`,
          {
            course_id: this.editingRecommendation.course_id,
            option_id: this.editingRecommendation.option_id
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Authorization': 'Bearer 4794|kHRenaMw73hAtD2PDbub0vFwqPKZX273Svu1YdQr7eab0fec'
            }
          }
        );
        
        if (response.data.status === 'success') {
          // Refresh the data
          this.fetchRecommendations();
          this.showEditModal = false;
          // Reset form
          this.editingRecommendation = {
            id: null,
            course_id: null,
            option_id: null
          };
        } else {
          alert('Failed to update recommendation');
        }
      } catch (err) {
        alert(err.message || 'An error occurred while updating the recommendation');
      } finally {
        this.updating = false;
      }
    },
    async deleteRecommendation(id) {
      if (!confirm('Are you sure you want to delete this recommendation?')) {
        return;
      }

      try {
        const response = await axios.delete(`https://api.kewaar.com/api/admin/recommendation-matrix/${id}`, {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': 'Bearer 4794|kHRenaMw73hAtD2PDbub0vFwqPKZX273Svu1YdQr7eab0fec'
          }
        });
        
        if (response.data.status === 'success') {
          // Refresh the data after successful deletion
          this.fetchRecommendations();
        } else {
          alert('Failed to delete recommendation');
        }
      } catch (err) {
        alert(err.message || 'An error occurred while deleting the recommendation');
      }
    },
    async createRecommendation() {
      if (!this.newRecommendation.course_id || !this.newRecommendation.option_id) {
        alert('Please fill in all fields');
        return;
      }

      this.creating = true;
      try {
        const response = await axios.post('https://api.kewaar.com/api/admin/recommendation-matrix', 
          this.newRecommendation,
          {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Authorization': 'Bearer 4794|kHRenaMw73hAtD2PDbub0vFwqPKZX273Svu1YdQr7eab0fec'
            }
          }
        );
        
        if (response.data.status === 'success') {
          // Refresh the data
          this.fetchRecommendations();
          this.showCreateModal = false;
          // Reset form
          this.newRecommendation = {
            course_id: null,
            option_id: null
          };
        } else {
          alert('Failed to create recommendation');
        }
      } catch (err) {
        alert(err.message || 'An error occurred while creating the recommendation');
      } finally {
        this.creating = false;
      }
    },
    async fetchRecommendations() {
      this.loading = true;
      try {
        const response = await axios.get('https://api.kewaar.com/api/admin/recommendation-matrix', {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': 'Bearer 4794|kHRenaMw73hAtD2PDbub0vFwqPKZX273Svu1YdQr7eab0fec'
          }
        });
        
        if (response.data.status === 'success') {
          this.recommendationData = response.data.data;
        } else {
          this.error = 'Failed to load recommendation data';
        }
      } catch (err) {
        this.error = err.message || 'An error occurred while fetching the data';
      } finally {
        this.loading = false;
      }
    },
    handleSearch: debounce(function() {
      this.fetchRecommendations();
    }, 300)
  },
  async created() {
    this.fetchRecommendations();
  }
};
</script>

<style scoped>
.recommendation-container {
  height: 100%;
  background-color: #f8fafc;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 0 1rem;
}

.header h1 {
  font-size: 1.875rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.search-container {
  width: 300px;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
  background-color: white;
  transition: all 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: #4f46e5;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
}

.statistics-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;
  padding: 0 1rem;
}

.stat-card {
  background: white;
  border-radius: 8px;
  padding: 1.25rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.stat-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-icon.total-recommendations {
  background: rgba(99, 102, 241, 0.1);
  color: #6366f1;
}

.stat-icon.top-courses {
  background: rgba(34, 197, 94, 0.1);
  color: #22c55e;
}

.stat-icon.breathing {
  background: rgba(234, 179, 8, 0.1);
  color: #eab308;
}

.stat-info h3 {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
  margin: 0;
}

.stat-info p {
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
  margin: 0.25rem 0 0;
}

.content {
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  margin: 0 1rem;
}

.loading {
  padding: 4rem;
  text-align: center;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top-color: #4f46e5;
  border-radius: 50%;
  margin: 0 auto 1rem;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error {
  padding: 2rem;
  text-align: center;
  color: #dc2626;
}

.retry-button {
  margin-top: 1rem;
  padding: 0.5rem 1rem;
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.retry-button:hover {
  background: #b91c1c;
}

.table-container {
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th {
  background: white;
  padding: 0.75rem 1rem;
  text-align: left;
  font-weight: 500;
  color: #6b7280;
  font-size: 0.875rem;
  border-bottom: 1px solid #e5e7eb;
}

td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e5e7eb;
  color: #374151;
  font-size: 0.875rem;
}

tr:hover {
  background-color: #f9fafb;
}

.course-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.course-title {
  font-weight: 500;
  color: #1f2937;
}

.mobile-info {
  color: #6b7280;
  font-size: 0.75rem;
}

.category-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  background: #f3f4f6;
  color: #374151;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
}

.status-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
}

.status-badge.top-course {
  background: #d1fae5;
  color: #065f46;
}

.status-badge.breathing {
  background: #fef3c7;
  color: #92400e;
}

.actions {
  display: flex;
  gap: 0.5rem;
}

.action-btn {
  padding: 0.5rem;
  background: none;
  border: none;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
}

.action-btn svg {
  width: 16px;
  height: 16px;
}

.action-btn.edit {
  color: #4f46e5;
}

.action-btn.edit:hover {
  background: rgba(79, 70, 229, 0.1);
}

.action-btn.delete {
  color: #dc2626;
}

.action-btn.delete:hover {
  background: rgba(220, 38, 38, 0.1);
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
  color: #1a1a1a;
}

.close-button {
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  color: #64748b;
  transition: color 0.2s;
}

.close-button:hover {
  color: #1a1a1a;
}

.close-button svg {
  width: 20px;
  height: 20px;
}

.modal-body {
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #1a1a1a;
  margin-bottom: 0.5rem;
}

.form-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  transition: all 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: #4f46e5;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
}

.cancel-button {
  padding: 0.75rem 1.5rem;
  background: #f1f5f9;
  color: #475569;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.cancel-button:hover {
  background: #e2e8f0;
}

.submit-button {
  padding: 0.75rem 1.5rem;
  background: #4f46e5;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.submit-button:hover:not(:disabled) {
  background: #4338ca;
}

.submit-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.create-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #4f46e5;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.create-button:hover {
  background: #4338ca;
}

.create-button svg {
  width: 16px;
  height: 16px;
}

@media (max-width: 768px) {
  .recommendation-container {
    padding: 1rem;
  }
  
  .header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
  
  .header h1 {
    font-size: 1.5rem;
  }
}
</style> 